/**
 * Utility functions for body part mapping and color coding
 */

/**
 * Get color for body part based on mole count
 * @param {string} bodyPart - Body part code
 * @param {Array} molesData - Array of moles
 * @returns {string} - Hex color code
 */
export const getBodyPartColor = (bodyPart, molesData = []) => {
  // Filter out moles with null/None body parts for SVG coloring
  const validMoles = molesData.filter(
    (mole) => mole.body_part && mole.body_part !== 'None' && mole.body_part !== null,
  );
  
  // Count moles for exact match only - no cross-orientation counting
  const moleCount = validMoles.filter((mole) => mole.body_part === bodyPart)?.length || 0;
  
  const color = moleCount === 0 ? '#e0e0e0' : 
                moleCount <= 1 ? '#90EE90' : 
                moleCount <= 3 ? '#FFD700' : '#FF6B6B';
  
  return color;
};

/**
 * Get opacity for body part based on selection
 * @param {string} bodyPart - Body part code
 * @param {string} selectedBodyPart - Currently selected body part
 * @returns {number} - Opacity value
 */
export const getBodyPartOpacity = (bodyPart, selectedBodyPart) => {
  return selectedBodyPart === bodyPart ? 0.8 : 0.6;
};

/**
 * Map front parts to back equivalents
 * @param {string} frontPart - Front body part code
 * @param {string} viewType - 'front' or 'back'
 * @returns {string} - Mapped body part code
 */
export const getBodyPartForView = (frontPart, viewType) => {
  if (viewType === 'back') {
    const backMapping = {
      head: 'head_back',
      neck: 'neck_back',
      chest: 'upper_back',
      abdomen: 'lower_back',
      face: 'head_back', // Face maps to head_back in back view
      left_upper_arm: 'left_upper_arm_back',
      right_upper_arm: 'right_upper_arm_back',
      left_forearm: 'left_forearm_back',
      right_forearm: 'right_forearm_back',
      left_hand: 'left_hand_back',
      right_hand: 'right_hand_back',
      left_thigh: 'left_thigh_back',
      right_thigh: 'right_thigh_back',
      left_lower_leg: 'left_lower_leg_back',
      right_lower_leg: 'right_lower_leg_back',
      left_foot: 'left_foot_back',
      right_foot: 'right_foot_back',
      hips: 'hips_back',
    };
    return backMapping[frontPart] || frontPart;
  }
  return frontPart;
};

// Polish body part translations fallback map
const POLISH_BODY_PARTS = {
  head: 'Głowa',
  head_back: 'Głowa (tył)',
  neck: 'Szyja',
  neck_back: 'Szyja (tył)',
  face: 'Twarz',
  chest: 'Klatka piersiowa',
  upper_back: 'Górna część pleców',
  abdomen: 'Brzuch',
  lower_back: 'Dolna część pleców',
  hips: 'Biodra',
  hips_back: 'Biodra (tył)',
  left_upper_arm: 'Lewe ramię (górna część)',
  right_upper_arm: 'Prawe ramię (górna część)',
  left_upper_arm_back: 'Lewe ramię (górna część, tył)',
  right_upper_arm_back: 'Prawe ramię (górna część, tył)',
  left_forearm: 'Lewe przedramię',
  right_forearm: 'Prawe przedramię',
  left_forearm_back: 'Lewe przedramię (tył)',
  right_forearm_back: 'Prawe przedramię (tył)',
  left_hand: 'Lewa dłoń',
  right_hand: 'Prawa dłoń',
  left_hand_back: 'Lewa dłoń (tył)',
  right_hand_back: 'Prawa dłoń (tył)',
  left_thigh: 'Lewe udo',
  right_thigh: 'Prawe udo',
  left_thigh_back: 'Lewe udo (tył)',
  right_thigh_back: 'Prawe udo (tył)',
  left_lower_leg: 'Lewa golenie',
  right_lower_leg: 'Prawa golenie',
  left_lower_leg_back: 'Lewa golenie (tył)',
  right_lower_leg_back: 'Prawa golenie (tył)',
  left_foot: 'Lewa stopa',
  right_foot: 'Prawa stopa',
  left_foot_back: 'Lewa stopa (tył)',
  right_foot_back: 'Prawa stopa (tył)',
};

/**
 * Get human-readable body part name from API data or code
 * @param {string} bodyPartCode - Body part code
 * @param {Object} bodyParts - Body parts data from API
 * @param {boolean} isLoading - Whether body parts are still loading
 * @param {string} language - Current language ('pl' or 'en')
 * @returns {string} - Human-readable name
 */
export const getBodyPartName = (bodyPartCode, bodyParts = {}, isLoading = false, language = 'en') => {
  // Avoid showing misleading "(loading...)" if we already have data for the other side
  if (isLoading && Object.keys(bodyParts).length === 0) {
    // If Polish and we have a translation, use it
    if (language.toLowerCase().startsWith('pl') && POLISH_BODY_PARTS[bodyPartCode]) {
      return POLISH_BODY_PARTS[bodyPartCode];
    }
    return bodyPartCode.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  }

  // Try to find in body parts data from API
  // API returns: {categoryName: [{code, name}, ...]}
  for (const category in bodyParts) {
    if (Array.isArray(bodyParts[category])) {
      const part = bodyParts[category].find((part) => part.code === bodyPartCode);
      if (part) {
        return part.name;
      }
    }
  }

  // Fallback: try mapping common back-view codes to their front equivalents
  const backToFrontMap = {
    head_back: 'head',
    neck_back: 'neck',
    upper_back: 'chest',
    lower_back: 'abdomen',
    hips_back: 'hips',
    left_upper_arm_back: 'left_upper_arm',
    right_upper_arm_back: 'right_upper_arm',
    left_forearm_back: 'left_forearm',
    right_forearm_back: 'right_forearm',
    left_hand_back: 'left_hand',
    right_hand_back: 'right_hand',
    left_thigh_back: 'left_thigh',
    right_thigh_back: 'right_thigh',
    left_lower_leg_back: 'left_lower_leg',
    right_lower_leg_back: 'right_lower_leg',
    left_foot_back: 'left_foot',
    right_foot_back: 'right_foot',
  };

  const normalizedCode = backToFrontMap[bodyPartCode] || bodyPartCode.replace(/_back$/, '');
  if (normalizedCode && normalizedCode !== bodyPartCode) {
    for (const category in bodyParts) {
      if (Array.isArray(bodyParts[category])) {
        const part = bodyParts[category].find((part) => part.code === normalizedCode);
        if (part) {
          return part.name;
        }
      }
    }
  }

  // Final fallback: use Polish translations if available and language is Polish
  if (language.toLowerCase().startsWith('pl') && POLISH_BODY_PARTS[bodyPartCode]) {
    return POLISH_BODY_PARTS[bodyPartCode];
  }

  // Also check if the normalized code has a Polish translation
  if (language.toLowerCase().startsWith('pl') && normalizedCode && POLISH_BODY_PARTS[normalizedCode]) {
    return POLISH_BODY_PARTS[normalizedCode];
  }

  // Fallback to body part code with formatting
  return bodyPartCode.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
};
